import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import FormDialog, { PromptDialog } from '../common/FormDialog';
import CompanyForm from '../Companies/CompanyForm';
import { vehiclesApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';

const byName = (a, b) => a.name.localeCompare(b.name);
const sortMakes = (makes) => makes.map((m) => ({ ...m, models: [...(m.models || [])].sort(byName) })).sort(byName);

export default function VehicleForm({ id = 0, onClose, onSaved }) {
    const alerts = useAlerts();
    const companies = useSelector((s) => s.companies.list);
    const [f, setF] = useState({ id: 0, identificationNumber: '', licenceNumber: '', year: '', company: null, make: null, model: null });
    const [makes, setMakes] = useState([]);
    const [busy, setBusy] = useState(true);
    const [dirty, setDirty] = useState(false);
    const [title, setTitle] = useState(id ? '' : 'Novo vozilo');
    const touched = dirty; // neispravna polja se označavaju posle prve izmene
    const [prompt, setPrompt] = useState(null); // 'make' | 'model'
    const [companyForm, setCompanyForm] = useState(false);

    const loadMakes = async () => sortMakes(await vehiclesApi.makes());

    useEffect(() => {
        (async () => {
            try {
                const ms = await loadMakes();
                setMakes(ms);
                if (id) {
                    const v = await vehiclesApi.get(id);
                    const make = ms.find((m) => m.id === (v.make && v.make.id)) || ms[0] || null;
                    const model = make && make.models.find((m) => m.id === (v.model && v.model.id));
                    setF({ ...v, year: v.year == null ? '' : String(v.year), make, model: model || null });
                    setTitle(`Vozilo:  ${v.identificationNumber} [${v.licenceNumber}]`);
                } else {
                    const make = ms[0] || null;
                    setF((x) => ({ ...x, make, model: (make && make.models[0]) || null }));
                }
            } catch { onClose(); }
            setBusy(false);
        })();
    }, [id]);

    const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setDirty(true); };
    const setMake = (make) => { setF((x) => ({ ...x, make, model: (make && make.models[0]) || null })); setDirty(true); };

    const addMake = async (name) => {
        setPrompt(null);
        try {
            const made = await vehiclesApi.createMake(name);
            const ms = await loadMakes();
            setMakes(ms);
            setMake(ms.find((m) => m.id === made.id) || ms[0]);
        } catch { /* toast */ }
    };
    const addModel = async (name) => {
        setPrompt(null);
        try {
            const made = await vehiclesApi.createModel(name, f.make.id);
            const ms = await loadMakes();
            setMakes(ms);
            const make = ms.find((m) => m.id === f.make.id);
            setF((x) => ({ ...x, make, model: make.models.find((m) => m.id === made.id) || null }));
        } catch { /* toast */ }
    };

    const valid = f.identificationNumber.trim() && f.licenceNumber.trim() && String(f.year).trim() && f.make && f.model;
    const bad = (k) => touched && !String(f[k] || '').toString().trim();

    const save = async () => {
        if (!valid) return;
        const body = { ...f, year: Number(f.year), company: f.company && f.company.id != null ? f.company : null };
        try {
            f.id ? await vehiclesApi.update(body) : await vehiclesApi.create(body);
            alerts.success(f.id ? 'Ažuriranje vozila uspešno' : 'Dodavanje vozila uspešno');
            if (onSaved) await onSaved();
            onClose();
        } catch { /* toast */ }
    };

    return (
        <>
            <FormDialog visible title={title} canSave={!!valid}
                onClose={onClose} onSave={save} dirty={dirty} busy={busy} width="42rem">
                <div className="form-grid cols-2">
                    <div className="field">
                        <label htmlFor="veh-identificationNumber">Broj šasije<span className="req" aria-hidden="true">*</span></label>
                        <InputText id="veh-identificationNumber" value={f.identificationNumber} onChange={(e) => set('identificationNumber', e.target.value)} className={bad('identificationNumber') ? 'p-invalid' : ''} />
                    </div>
                    <div className="field">
                        <label htmlFor="veh-licenceNumber">Reg. broj<span className="req" aria-hidden="true">*</span></label>
                        <InputText id="veh-licenceNumber" value={f.licenceNumber} onChange={(e) => set('licenceNumber', e.target.value)} className={bad('licenceNumber') ? 'p-invalid' : ''} />
                    </div>
                    <div className="field">
                        <label htmlFor="veh-year">Godina proizvodnje<span className="req" aria-hidden="true">*</span></label>
                        <InputText id="veh-year" keyfilter="pint" maxLength={4} value={f.year} onChange={(e) => set('year', e.target.value)} className={bad('year') ? 'p-invalid' : ''} />
                    </div>
                    <div className="field">
                        <label htmlFor="veh-company">Kompanija</label>
                        <div className="row-inline">
                            <Dropdown inputId="veh-company" value={companies.find((c) => f.company && c.id === f.company.id) || (companies[0] || null)}
                                options={companies} optionLabel="name" dataKey="id" filter emptyFilterMessage="Nema rezultata"
                                onChange={(e) => set('company', e.value)} />
                            <Button type="button" icon="pi pi-plus" rounded text tooltip="Dodaj kompaniju" aria-label="Dodaj kompaniju" onClick={() => setCompanyForm(true)} />
                        </div>
                    </div>
                    <div className="field">
                        <label htmlFor="veh-make">Marka<span className="req" aria-hidden="true">*</span></label>
                        <div className="row-inline">
                            <Dropdown inputId="veh-make" value={f.make} options={makes} optionLabel="name" dataKey="id" filter emptyFilterMessage="Nema rezultata"
                                onChange={(e) => setMake(e.value)} className={touched && !f.make ? 'p-invalid' : ''} />
                            <Button type="button" icon="pi pi-plus" rounded text tooltip="Dodaj marku" aria-label="Dodaj marku" onClick={() => setPrompt('make')} />
                        </div>
                    </div>
                    <div className="field">
                        <label htmlFor="veh-model">Model<span className="req" aria-hidden="true">*</span></label>
                        <div className="row-inline">
                            <Dropdown inputId="veh-model" value={f.model} options={(f.make && f.make.models) || []} optionLabel="name" dataKey="id" filter emptyFilterMessage="Nema rezultata"
                                onChange={(e) => set('model', e.value)} className={touched && !f.model ? 'p-invalid' : ''} />
                            <Button type="button" icon="pi pi-plus" rounded text tooltip="Dodaj model" aria-label="Dodaj model" disabled={!f.make} onClick={() => setPrompt('model')} />
                        </div>
                    </div>
                </div>
            </FormDialog>
            <PromptDialog visible={prompt === 'make'} title="Dodaj marku" label="Marka" onClose={() => setPrompt(null)} onOk={addMake} />
            <PromptDialog visible={prompt === 'model'} title="Dodaj model" label="Model" onClose={() => setPrompt(null)} onOk={addModel} />
            {companyForm && <CompanyForm onClose={() => setCompanyForm(false)} onSaved={null} />}
        </>
    );
}
